import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of, EMPTY } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ReferenceScheme } from 'src/app/reference-scheme/models/reference-scheme.model';
import { LayerContextKeys } from 'src/app/shared/constants';

import * as feedAdviceActions from './feed-advice.actions';

import { ProductService } from '../../product/services/product.service';
import * as productActions from '../../product/store/actions/product.actions';
import { ReferenceSchemeService } from '../../reference-scheme/services/reference-scheme.service';
import { Product } from '../../shared/models';
import { SettingsServiceHelper } from '../../shared/services/settingsHelperService';
import {
  FeedAdvice,
  FeedAdviceModuleState,
  UpsertFeedAdviceResponse,
} from '../models/feed-advice.model';
import { FeedAdviceService } from '../services/feed-advice.service';

@Injectable()
export class FeedAdviceEffects {
  constructor(
    private actions$: Actions,
    private store: Store<FeedAdviceModuleState>,
    private router: Router,
    private dialogService: DialogService,
    @Inject(LocaleService) private locale: LocaleService,
    private feedAdviceService: FeedAdviceService,
    private productService: ProductService,
    private referenceSchemeService: ReferenceSchemeService,
    private settingsService: SettingsServiceHelper,
  ) {}

  /* LOAD DATA EFFECTS */
  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadData),
      concatMap(() => {
        return of(
          feedAdviceActions.clearFeedAdviceState(),
          productActions.loadProductsRequest(),
          feedAdviceActions.loadFeedAdvicesRequest(),
        );
      }),
    ),
  );

  loadFeedAdvicesRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadFeedAdvicesRequest),
      mergeMap(() => {
        return this.feedAdviceService.getFeedAdvices().pipe(
          switchMap((feedAdvices: FeedAdvice[]) =>
            of(
              feedAdviceActions.loadFeedAdvicesSuccess({ feedAdvices }),
              feedAdviceActions.loadProductsByCountryRequest(),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.loadFeedAdvicesFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  loadProductsByCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadProductsByCountryRequest),
      withLatestFrom(this.store.select(UserSelectors.selectLocation)),
      filter(([_, location]) => location !== undefined),
      exhaustMap(([_, location]) =>
        this.productService.getProducts().pipe(
          switchMap((products) =>
            of(
              feedAdviceActions.loadProductsByCountrySuccess({
                products: (products as Product[]).filter(
                  (x) => x?.country === location?.country.toString(),
                ),
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.loadProductsByCountryFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadReferenceSchemesByCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.loadReferenceSchemesByCountryRequest),
      withLatestFrom(this.store.select(UserSelectors.selectLocation)),
      filter(([_, location]) => location !== undefined),
      exhaustMap(([_, location]) =>
        this.referenceSchemeService.getReferenceSchemes(location?.country).pipe(
          switchMap((referenceSchemes: ReferenceScheme[]) =>
            of(
              feedAdviceActions.loadReferenceSchemesByCountrySuccess({
                referenceSchemes,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.loadProductsByCountryFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setBaseFeedAdviceRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.setBaseFeedAdviceRequest),
      exhaustMap((baseFeedAdvice) => {
        this.settingsService.setSettings(
          LayerContextKeys.REFERENCE_FEED_ADVICE,
          baseFeedAdvice.feedAdvice,
          SettingsStorage.AppLocal,
        );
        return of(feedAdviceActions.setBaseFeedAdviceSuccess(baseFeedAdvice));
      }),
    ),
  );

  /* NAVIGATION EFFECTS */
  createFromReferenceAdviceNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.createFromReferenceAdviceNavigation),
        exhaustMap(() => of(feedAdviceActions.loadReferenceSchemesByCountryRequest)),
        tap(() => {
          this.router.navigate(['feed-advice', 'create-from-reference']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  createAdviceNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.createAdviceNavigation),
        tap(() => {
          this.router.navigate(['feed-advice', 'create']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  editAdviceNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.editAdviceNavigation),
        tap((action) => {
          this.router.navigate(['feed-advice', 'edit', action.feedAdvice.id]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  viewAdviceNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.viewAdviceNavigation),
        tap((action) => {
          this.router.navigate(['feed-advice', 'view', action.feedAdvice.id]);
        }),
      ),
    {
      dispatch: false,
    },
  );

  /* UPSERT EFFECTS */
  upsertFeedAdviceRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.upsertFeedAdviceRequest),
      exhaustMap(({ feedAdvice }) =>
        this.feedAdviceService.upsertFeedAdvice(feedAdvice).pipe(
          map((response: UpsertFeedAdviceResponse) =>
            feedAdviceActions.upsertFeedAdviceSuccess({
              feedAdvice: {
                ...feedAdvice,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.upsertFeedAdviceFailed({ error }),
              feedAdviceActions.loadFeedAdvicesRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
  upsertFeedAdviceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedAdviceActions.upsertFeedAdviceSuccess),
        tap((_) => {
          this.router.navigate(['feed-advice']);
        }),
      ),
    {
      dispatch: false,
    },
  );

  /* DELETE EFFECTS */
  deleteAdviceConfirm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.deleteAdviceConfirm),
      exhaustMap(({ feedAdvice }) =>
        this.dialogService
          .confirmWarning({
            text: this.locale.translate(`feed-advice.dialog.deleteConfirm`),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  feedAdviceActions.deleteAdviceRequest({
                    feedAdviceId: feedAdvice.id,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteAdviceRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(feedAdviceActions.deleteAdviceRequest),
      mergeMap((action) =>
        this.feedAdviceService.deleteFeedAdvice(action.feedAdviceId).pipe(
          map(() => feedAdviceActions.deleteAdviceSuccess({ feedAdviceId: action.feedAdviceId })),
          catchError((error: HttpErrorResponse) =>
            of(
              feedAdviceActions.deleteAdviceFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
